import { useInfiniteQuery } from 'react-query';
import { baseApiUrl, fetcher, ServerErrorResponse, createParamString } from 'data_sources/constants';
import type { User } from 'views/App/Portal/LexID/Users/_types/user';
import { ApiCollectionResponse } from 'types/api_response';

export default function useRoleUsers<T extends User>(
  organizationId: string,
  roleId: string,
  params = {},
  options = {},
  onError?: (errors: ServerErrorResponse) => void
) {
  const url = `${baseApiUrl()}/lexid/admin-role/v1/organizations/${organizationId}/roles/${roleId}/users`;
  return useInfiniteQuery<ApiCollectionResponse<T>, ServerErrorResponse>(
    ['role_users', organizationId, roleId],
    async ({ pageParam, signal }): Promise<ApiCollectionResponse<T>> => {
      try {
        const paramString = createParamString(params);
        const data: ApiCollectionResponse<T> = await fetcher({
          method: 'get',
          url: pageParam || `${url}?${paramString}`,
          options: { signal },
        });

        return data;
      } catch (error: unknown) {
        if (onError && typeof error === 'object' && error !== null && 'errorDetails' in error) {
          onError(error as ServerErrorResponse);
        }
        throw error;
      }
    },
    {
      keepPreviousData: true,
      enabled: !!organizationId && !!roleId,
      getNextPageParam: (lastPage) => {
        if (lastPage.offset && lastPage.offset < (lastPage.count || 0) && lastPage.content.length > 0) {
          const paramString = createParamString({
            ...params,
            offset: lastPage.offset,
          });
          return `${url}?${paramString}`;
        }
      },
      ...options,
    }
  );
}
